import { combineReducers } from 'redux';

import type { Reducer } from 'Lib/reduxType';

import {
  APP_OFFLINE,
  APP_ONLINE,
  DEACTIVATE_TAB,
  DEVICE_FLAGGED_FOR_DOB_VIOLATION,
  DEVICE_UNFLAGGED_FOR_DOB_VIOLATION,
  GET_EXPERIMENTS_SUCCESS,
  SET_APP_SOURCE,
  SET_AUTH_ATTRIBUTION_DATA,
  SET_AV_PERMISSION_STATUS,
  SET_BRANDING,
  SET_DOB_VIOLATION_TIMEOUT,
} from 'Store/app/constants';
import { LIFECYCLE_ACTION_TYPE_PREFIX } from 'Store/constants';
import { LOGOUT_SUCCESS } from 'Store/mfa/constants';
import { FETCH_PROFILE_SUCCESS } from 'Store/profile/constants';
import { COMPLETE_3DS, SET_DEEPLINK_VERIFICATION_INFO } from 'Store/user/constants';

import initialState from './initialState';
import type { AppState } from './types';

const activeTabReducer: Reducer<AppState['activeTab']> = (
  state = initialState.activeTab,
  action
) => {
  switch (action.type) {
    case DEACTIVATE_TAB:
      return false;
    default:
      return state;
  }
};

const is3dsCompleteReducer: Reducer<AppState['is3dsComplete']> = (
  state = initialState.is3dsComplete,
  action
) => {
  switch (action.type) {
    case COMPLETE_3DS:
      return true;
    default:
      return state;
  }
};

const lifecyclesReducer: Reducer<AppState['lifecycles']> = (
  state = initialState.lifecycles,
  action
) => {
  if (action.type.startsWith(LIFECYCLE_ACTION_TYPE_PREFIX)) {
    return {
      ...state,
      ...action.payload,
    };
  }
  return state;
};

const offlineReducer: Reducer<AppState['offline']> = (state = initialState.offline, action) => {
  switch (action.type) {
    case APP_OFFLINE:
      return true;
    case APP_ONLINE:
      return false;
    default:
      return state;
  }
};

const profileLoadedReducer: Reducer<AppState['profileLoaded']> = (
  state = initialState.profileLoaded,
  action
) => {
  switch (action.type) {
    case FETCH_PROFILE_SUCCESS:
      return true;
    case LOGOUT_SUCCESS:
      return false;
    default:
      return state;
  }
};

const preAuthLeversLoadedReducer: Reducer<AppState['preAuthLeversLoaded']> = (
  state = initialState.preAuthLeversLoaded,
  action
) => {
  switch (action.type) {
    case GET_EXPERIMENTS_SUCCESS:
      return true;
    default:
      return state;
  }
};

const dobViolationDaysLeftReducer: Reducer<AppState['dobViolationDaysLeft']> = (
  state = initialState.dobViolationDaysLeft,
  action
) => {
  switch (action.type) {
    case SET_DOB_VIOLATION_TIMEOUT:
      return action.payload;
    default:
      return state;
  }
};

const deviceFlaggedForDobViolationReducer: Reducer<AppState['deviceFlaggedForDobViolation']> = (
  state = initialState.deviceFlaggedForDobViolation,
  action
) => {
  switch (action.type) {
    case DEVICE_FLAGGED_FOR_DOB_VIOLATION:
      return true;
    case DEVICE_UNFLAGGED_FOR_DOB_VIOLATION:
      return false;
    default:
      return state;
  }
};

const avPermissionStatusReducer: Reducer<AppState['avPermissionStatus']> = (
  state = initialState.avPermissionStatus,
  action
) => {
  switch (action.type) {
    case SET_AV_PERMISSION_STATUS:
      return action.payload;
    default:
      return state;
  }
};

const brandingReducer: Reducer<AppState['branding']> = (state = initialState.branding, action) => {
  switch (action.type) {
    case SET_BRANDING:
      return action.payload;
    default:
      return state;
  }
};

const authAttributionDataReducer: Reducer<AppState['authAttributionData']> = (
  state = initialState.authAttributionData,
  action
) => {
  switch (action.type) {
    case SET_AUTH_ATTRIBUTION_DATA:
      return action.payload;
    default:
      return state;
  }
};

const appSourceReducer: Reducer<AppState['appSource']> = (
  state = initialState.appSource,
  action
) => {
  switch (action.type) {
    case SET_APP_SOURCE:
      return action.payload;
    default:
      return state;
  }
};

const deeplinkSourceReducer: Reducer<AppState['deeplinkSource']> = (
  state = initialState.deeplinkSource,
  action
) => {
  switch (action.type) {
    case SET_DEEPLINK_VERIFICATION_INFO:
      return action?.payload?.type;
    default:
      return state;
  }
};

export const reducer = combineReducers({
  appSource: appSourceReducer,
  avPermissionStatus: avPermissionStatusReducer,
  activeTab: activeTabReducer,
  branding: brandingReducer,
  deviceFlaggedForDobViolation: deviceFlaggedForDobViolationReducer,
  dobViolationDaysLeft: dobViolationDaysLeftReducer,
  is3dsComplete: is3dsCompleteReducer,
  lifecycles: lifecyclesReducer,
  offline: offlineReducer,
  preAuthLeversLoaded: preAuthLeversLoadedReducer,
  profileLoaded: profileLoadedReducer,
  authAttributionData: authAttributionDataReducer,
  deeplinkSource: deeplinkSourceReducer,
});
